import React, { useEffect, useState } from 'react';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

/**
 * Horizontal step-by-step progress bar displayed above the terminal
 * while a post-exploitation chain is running.
 *
 * Props:
 *   chainId    – current chain id
 *   chainName  – human label
 *   steps      – array of { name, status: 'pending' | 'running' | 'done' | 'error' }
 *   visible    – show/hide
 */
const ChainProgressBar = ({ chainId, chainName, steps = [], visible = false }) => {
  const [show, setShow] = useState(visible);

  // Auto-hide 3s after all steps are done/error
  useEffect(() => {
    if (!visible) {
      setShow(false);
      return;
    }
    setShow(true);

    const allFinished = steps.length > 0 && steps.every(s => s.status === 'done' || s.status === 'error');
    if (allFinished) {
      const timer = setTimeout(() => setShow(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [visible, steps]);

  if (!show || steps.length === 0) return null;

  const statusIcon = (status) => {
    switch (status) {
      case 'done':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-400" />;
      case 'running':
        return <Loader2 className="w-4 h-4 text-yellow-400 animate-spin" />;
      default:
        return <span className="w-4 h-4 rounded-full bg-gray-600 inline-block" />;
    }
  };

  const statusColor = (status) => {
    switch (status) {
      case 'done': return 'border-green-500/50 bg-green-500/10';
      case 'error': return 'border-red-500/50 bg-red-500/10';
      case 'running': return 'border-yellow-500/50 bg-yellow-500/10';
      default: return 'border-gray-700 bg-gray-800/50';
    }
  };

  return (
    <div className="bg-brand-black-light rounded-lg border border-brand-green/20 p-3 mb-3 animate-fadeIn">
      <div className="flex items-center gap-2 mb-2">
        <Loader2 className="w-4 h-4 text-brand-green animate-spin" />
        <span className="text-sm font-bold text-brand-green">{chainName || chainId}</span>
      </div>
      <div className="flex items-center gap-1 overflow-x-auto">
        {steps.map((step, i) => (
          <React.Fragment key={i}>
            <div
              className={`flex items-center gap-1.5 px-2 py-1 rounded border text-xs whitespace-nowrap ${statusColor(step.status)}`}
            >
              {statusIcon(step.status)}
              <span className={step.status === 'running' ? 'text-yellow-300' : step.status === 'done' ? 'text-green-300' : 'text-gray-400'}>
                {step.name}
              </span>
            </div>
            {i < steps.length - 1 && (
              <span className="text-gray-600 text-xs flex-shrink-0">→</span>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default ChainProgressBar;
